// The designer inspector's width (spec 6.3.4): 300–560 px, stored in the user's layout object as
// `designer: { inspectorWidth }`, removed by Reset layout.
import { useCallback, useSyncExternalStore } from 'react';
import { layoutKey, readLayoutObject, storage, writeLayoutMember } from '../shell/layoutStore';

export const INSPECTOR_WIDTH = {
  min: 300,
  max: 560,
  /** The canvas keeps at least this much. */
  canvasMin: 320,
  step: 16,
  bigStep: 64,
  /** The default from 1440 px wide, and below it. */
  wide: 360,
  narrow: 320,
} as const;

const MEMBER = 'designer';
const listeners = new Set<() => void>();
let memo: { user: string; raw: string | null; width: number | null } | null = null;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The widest the inspector may be with `available` pixels for canvas and inspector together. */
export function maxInspectorWidth(available: number): number {
  return Math.max(INSPECTOR_WIDTH.min, Math.min(INSPECTOR_WIDTH.max, available - INSPECTOR_WIDTH.canvasMin));
}

export function clampInspectorWidth(width: number, max: number = INSPECTOR_WIDTH.max): number {
  return Math.round(Math.min(Math.max(width, INSPECTOR_WIDTH.min), Math.max(INSPECTOR_WIDTH.min, max)));
}

/** The stored width, or null when none (or an invalid one) is stored. Never throws. */
export function readInspectorWidth(user: string): number | null {
  const member = readLayoutObject(user)[MEMBER];
  const width = isObject(member) ? member.inspectorWidth : undefined;
  return typeof width === 'number' && Number.isFinite(width) ? clampInspectorWidth(width) : null;
}

/** Stores a width (null removes it), keeping the layout object's other members. */
export function writeInspectorWidth(user: string, width: number | null): void {
  const member = readLayoutObject(user)[MEMBER];
  const rest: Record<string, unknown> = isObject(member) ? { ...member } : {};
  delete rest.inspectorWidth;
  const next = width === null ? rest : { ...rest, inspectorWidth: clampInspectorWidth(width) };
  writeLayoutMember(user, MEMBER, Object.keys(next).length > 0 ? next : undefined);
  memo = null;
  for (const listener of listeners) listener();
}

function snapshot(user: string | null): number | null {
  if (!user) return null;
  let raw: string | null;
  try {
    raw = storage()?.getItem(layoutKey(user)) ?? null;
  } catch {
    raw = null;
  }
  if (memo && memo.user === user && memo.raw === raw) return memo.width;
  const width = readInspectorWidth(user);
  memo = { user, raw, width };
  return width;
}

/** The user's stored inspector width, following writes (also Reset layout). */
export function useStoredInspectorWidth(user: string | null): number | null {
  const subscribe = useCallback((listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => snapshot(user),
    () => null,
  );
}
